<template>
	<Dialog :visible="true" style="width: 50vw" modal :closable="false">
		<template #header>
			<div class="flex justify-between w-full">
				<PageLabel :helpPageName="'ProvisionalMaterial'">
					<h1>Add Provisional Material</h1>
				</PageLabel>
				<div class="flex gap-2">
					<Button label="Cancel" @click="$emit('cancel')" severity="danger" />
					<Button label="Save" @click="saveForm()" />
				</div>
			</div>
		</template>

		<div class="flex flex-col gap-4">
			<I2FormRow>
				<I2FormField label="Provisional Material Name *">
					<InputText
						ref="materialNameRef"
						v-model="formData.materialName"
						placeholder="Enter provisional material name"
						:class="{ 'p-invalid': isFieldInvalid('materialName') }"
					/>
				</I2FormField>
			</I2FormRow>

			<I2FormRow>
				<I2FormField label="Provisional Material Type *">
					<Select
						v-model="formData.materialTypeId"
						:options="materialTypes"
						option-label="materialTypeName"
						option-value="id"
						placeholder="Select Provisional Material Type"
						:filter="materialTypes.length > 10"
						:class="{ 'p-invalid': isFieldInvalid('materialTypeId') }"
						@change="onMaterialTypeChange"
					/>
				</I2FormField>
			</I2FormRow>

			<I2FormRow>
				<I2FormField label="Provisional Material CAS">
					<InputText v-model="formData.cas" placeholder="Enter provisional material CAS number (optional)" />
				</I2FormField>
			</I2FormRow>

			<I2FormRow>
				<I2FormField label="Temporary Proxy Material *">
					<Select
						v-model="formData.provisionalProxyMaterialId"
						:options="filteredProxyMaterials"
						option-label="materialName"
						option-value="id"
						:placeholder="
							!formData.materialTypeId
								? 'Select Provisional Material Type First'
								: hasAvailableProxyMaterials
									? 'Select Proxy Material'
									: 'No materials are available for the selected Provisional Material Type'
						"
						:disabled="!formData.materialTypeId"
						filter
						:filter-fields="['materialName', 'cas']"
						filter-placeholder="Search materials..."
						:class="{ 'p-invalid': isFieldInvalid('provisionalProxyMaterialId') }"
					>
						<template #option="slotProps">
							<div class="flex flex-col gap-1 py-1">
								<div class="font-semibold">{{ slotProps.option.materialName }}</div>
								<div class="flex flex-wrap gap-x-4 gap-y-1 text-xs opacity-70">
									<span v-if="slotProps.option.isProxy">
										<i class="pi pi-star mr-1 text-[10px]"></i>
										Proxy
									</span>
									<span v-if="slotProps.option.materialTypeName">
										<i class="pi pi-tag mr-1 text-[10px]"></i>
										{{ slotProps.option.materialTypeName }}
									</span>
									<span v-if="slotProps.option.cas">
										<i class="pi pi-info-circle mr-1 text-[10px]"></i>
										CAS: {{ slotProps.option.cas }}
									</span>
								</div>
							</div>
						</template>
					</Select>
					<div v-if="formData.materialTypeId && !hasAvailableProxyMaterials" class="mt-2 text-sm text-amber-700">
						No materials are available for the selected "Provisional Material Type"
					</div>
				</I2FormField>
			</I2FormRow>

			<I2FormRow>
				<I2FormField>
					<template #label>
						Rationale
						<i
							class="pi pi-info-circle ml-2"
							style="cursor: help"
							v-tooltip.right="
								'Explain why this provisional material is needed and why the selected proxy material is an appropriate temporary substitute.'
							"
						></i>
					</template>
					<Textarea v-model="formData.comments" rows="4" autoResize placeholder="Enter rationale (optional)" />
				</I2FormField>
			</I2FormRow>
		</div>
	</Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, nextTick } from "vue"
import { MgmRawMaterialsClient, MaterialType, MaterialListForTable, ProvisionalMaterialSaveRequest, Material } from "../../entities/NSwagGenerated"
import Dialog from "primevue/dialog"
import Button from "primevue/button"
import InputText from "primevue/inputtext"
import Select from "primevue/select"
import Textarea from "primevue/textarea"
import { z } from "zod/v4"
import { useErrorToaster, useToaster } from "@/composables/toaster"

const mgmRawMaterialsClient = new MgmRawMaterialsClient(import.meta.env.VITE_API_URL?.toString())

const props = defineProps({
	screenId: {
		type: String,
		required: true,
	},
})

const emit = defineEmits<{
	(e: "cancel"): void
	(e: "saved", material: Material): void
}>()

const materialNameRef = ref()
const saveAttempted = ref(false)
const materialTypes = ref([] as MaterialType[])
const materials = ref([] as MaterialListForTable[])

const formData = ref(
	new ProvisionalMaterialSaveRequest({
		materialName: "",
		materialTypeId: undefined,
		cas: "",
		comments: "",
		provisionalProxyMaterialId: undefined,
		screenId: props.screenId,
	})
)

const formValidationSchema = z.object({
	materialName: z.string().min(1),
	materialTypeId: z.string().uuid(),
	provisionalProxyMaterialId: z.string().uuid(),
})

const formValidation = computed(() => {
	return formValidationSchema.safeParse({
		materialName: formData.value.materialName,
		materialTypeId: formData.value.materialTypeId,
		provisionalProxyMaterialId: formData.value.provisionalProxyMaterialId,
	})
})

function normalizeValue(value?: string | null): string {
	return value?.trim().toLowerCase() ?? ""
}

const selectedMaterialType = computed(() => {
	const selectedTypeId = normalizeValue(formData.value.materialTypeId)
	return materialTypes.value.find((materialType) => normalizeValue(materialType.id) === selectedTypeId)
})

const filteredProxyMaterials = computed(() => {
	if (!formData.value.materialTypeId) {
		return []
	}

	const selectedTypeId = normalizeValue(formData.value.materialTypeId)
	const selectedTypeName = normalizeValue(selectedMaterialType.value?.materialTypeName)

	// Show every material in the chosen category, including any proxy materials in that category.
	return materials.value.filter((material: MaterialListForTable) => {
		const candidateTypeId = normalizeValue(material.materialTypeId)
		const candidateTypeName = normalizeValue(material.materialTypeName)

		return (selectedTypeId !== "" && candidateTypeId === selectedTypeId) || (selectedTypeName !== "" && candidateTypeName === selectedTypeName)
	})
})

const hasAvailableProxyMaterials = computed(() => filteredProxyMaterials.value.length > 0)

function onMaterialTypeChange() {
	formData.value.provisionalProxyMaterialId = undefined as any
}

function isFieldInvalid(fieldName: "materialName" | "materialTypeId" | "provisionalProxyMaterialId"): boolean {
	if (!saveAttempted.value) {
		return false
	}
	if (formValidation.value.error) {
		return formValidation.value.error.issues.some((issue) => issue.path.some((part) => part === fieldName))
	}
	return false
}

async function saveForm() {
	saveAttempted.value = true

	if (!formValidation.value.success) {
		useToaster({
			severity: "error",
			summary: "Missing Required Fields",
			detail: "Please fill in all required fields.",
			life: 3000,
		})
		return
	}

	try {
		const request = new ProvisionalMaterialSaveRequest({
			materialName: formData.value.materialName,
			materialTypeId: formData.value.materialTypeId,
			cas: formData.value.cas || undefined,
			comments: formData.value.comments || undefined,
			provisionalProxyMaterialId: formData.value.provisionalProxyMaterialId,
			screenId: props.screenId,
		})

		const savedMaterial = await mgmRawMaterialsClient.saveProvisionalMaterial(request)

		useToaster({
			severity: "success",
			summary: "Success",
			detail: "Provisional material created successfully.",
			life: 3000,
		})

		emit("saved", savedMaterial)
	} catch (error) {
		useErrorToaster(error)
	}
}

// "API Synthetic" and "API Biologics" are reserved for productProcessProxy rows (the synthetic
// API/biologics process output), and "Intermediates" is reserved for intermediateStep rows - none
// of these are individual materials, so they must not be selectable here.
const reservedMaterialTypeNames = ["API Synthetic", "API Biologics", "Intermediates"]

async function loadMaterialTypes() {
	try {
		const result = await mgmRawMaterialsClient.mgmMaterialTypesAll()
		const filtered = result.filter(
			(mt) =>
				mt.materialTypeScreen?.some((mts) => mts.screenId?.toLowerCase() === props.screenId.toLowerCase() && mts.isActive) &&
				!reservedMaterialTypeNames.includes(mt.materialTypeName ?? "")
		)
		materialTypes.value = filtered.sort((a, b) => (a.materialTypeName ?? "").localeCompare(b.materialTypeName ?? ""))
	} catch (error) {
		useErrorToaster(error)
	}
}

async function loadMaterials() {
	try {
		const allMaterials = await mgmRawMaterialsClient.mgmGetAllRawMaterials(true)
		materials.value = allMaterials.sort((a, b) => (a.materialName ?? "").localeCompare(b.materialName ?? ""))
	} catch (error) {
		useErrorToaster(error)
	}
}

onMounted(async () => {
	await Promise.all([loadMaterialTypes(), loadMaterials()])
	nextTick(() => {
		materialNameRef.value?.$el?.focus()
	})
})
</script>
